/**
 * erpSpec — Italian ERP connectors (SAP OData / Zucchetti / TeamSystem).
 *
 * PR-INT-3: closes V1 parity gap for the "ERP italiano" tile that V1's
 * ``EnterpriseHubPage`` ships under the ERP section. Distinct from
 * ``accountingSpec`` (FiC + SAP Business One) which targets invoicing/
 * fatturazione SaaS, not full-stack ERP.
 *
 * v1 source-of-truth:
 *   ando-frontend/app/pages/EnterpriseHubPage.tsx (lines 186-225 — ERP tiles)
 *   ando-frontend/app/lib/connectors.ts (``ERP_CONNECTOR_KEYS``)
 *   ando-frontend/app/components/enterprise/ConnectorWizard.tsx (lines 209-215 — sap_odata)
 *
 * Tier / ICP fit (per ``feedback_connector_icp_fit_tiers.md``):
 *   - SAP OData                 — Tier 1, BE driver ships today (sap_odata).
 *   - Zucchetti                 — Tier 4, partner-gated → ``disabled: true``.
 *   - TeamSystem                — Tier 4, partner-gated → ``disabled: true``.
 *
 * Why ship the tier-4 vendors in the picker at all: keeps the catalog
 * honest for Sales conversations ("yes, we'll integrate Zucchetti once the
 * partner deal closes") AND lets the empty-state benefits grid carry the
 * "ERP italiano" value-prop without the user clicking through to a 4xx.
 * The ``disabled`` flag on ``VendorOption`` keeps the click suppressed so
 * partner-gated rows look "In arrivo", not "broken".
 *
 * BE surface (existing per V1):
 *   POST /api/v1/enterprise/connectors/erp (multipart) — the V2 unified
 *   adapter wraps it via ``createCapabilityApi`` like every other spec.
 */

import {
  type BaseConnection,
  type CapabilityWizardSpec,
  DEFAULT_DISABLED_REASON,
  type FieldSpec,
} from "@ando/ui";
import { i18n as i18nSingleton } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { Briefcase, Layers, Search, Server, Sparkles } from "lucide-react";

import { createCapabilityApi } from "../api/capabilityApi";

export interface ErpConnection extends BaseConnection {
  readonly connector_type?: string;
  readonly service_url?: string;
  readonly service_namespace?: string;
  readonly host?: string;
}

const iconCls = "h-4 w-4";

// ── SAP OData credentials — Tier 1 driver, fully wired BE-side ──────────
const sapOdataFields: readonly FieldSpec[] = [
  {
    name: "service_url",
    label: msg`Endpoint OData v4`,
    type: "url",
    required: true,
    placeholder:
      "https://sap.example.com/sap/opu/odata4/sap/api_business_partner/srvd_a2x/sap/businesspartner/0001",
    helperText: msg`URL completo dell'endpoint OData v4 (Business Suite, S/4HANA).`,
  },
  {
    name: "user",
    label: msg`Utente`,
    type: "string",
    required: true,
    placeholder: "ANDO_RFC_USER",
  },
  {
    name: "password",
    label: "Password",
    type: "password",
    required: true,
  },
  {
    name: "service_namespace",
    label: msg`Namespace servizio (opzionale)`,
    type: "string",
    required: false,
    placeholder: "API_BUSINESS_PARTNER",
    helperText: msg`Lascia vuoto per auto-discovery dal $metadata.`,
  },
];

// ── Stub form for partner-gated Tier-4 vendors. Rendered ONLY if the
//    ``disabled`` flag is ever flipped to ``false`` (driver lands). Kept
//    minimal — host/user/password — because the partner contract drives
//    the real field set. Without this, ConnectAccountDialog would crash
//    on a credentials_form response. ─────────────────────────────────────
const partnerGatedFields: readonly FieldSpec[] = [
  {
    name: "host",
    label: msg`Endpoint`,
    type: "url",
    required: true,
    placeholder: "https://erp.example.com",
    helperText: msg`Endpoint API del vendor — fornito dal partner.`,
  },
  {
    name: "user",
    label: msg`Utente`,
    type: "string",
    required: true,
  },
  {
    name: "password",
    label: "Password",
    type: "password",
    required: true,
  },
];

export const erpSpec: CapabilityWizardSpec<ErpConnection> = {
  capability: "erp",
  title: msg`ERP Italiano`,
  subtitle: "SAP OData (S/4HANA · Business Suite) · Zucchetti · TeamSystem",
  icon: <Server className="h-5 w-5" aria-hidden />,
  gdprDeleteWindowMinutes: 30,

  vendors: [
    {
      id: "sap_odata",
      label: "SAP OData",
      icon: <Server className={iconCls} aria-hidden />,
      description: msg`Endpoint OData v4 (Business Suite, S/4HANA)`,
    },
    {
      id: "zucchetti",
      label: "Zucchetti",
      icon: <Briefcase className={iconCls} aria-hidden />,
      description: msg`In arrivo · partner-gated`,
      disabled: true,
      disabledReason: DEFAULT_DISABLED_REASON,
    },
    {
      id: "teamsystem",
      label: "TeamSystem",
      icon: <Briefcase className={iconCls} aria-hidden />,
      description: msg`In arrivo · partner-gated`,
      disabled: true,
      disabledReason: DEFAULT_DISABLED_REASON,
    },
  ],

  // Per-vendor credentials steps (tagged with ``WizardStepSpec.vendor`` —
  // same discriminator pattern as ``databaseSpec``). The dialog picks the
  // matching step at submit-time based on which vendor the user chose in
  // ``AddAccountMenu``. Tier-4 vendors are wired with a placeholder form
  // so the wizard renders something coherent the moment the partner
  // deal lands and we flip ``disabled: false`` on the picker entry.
  steps: [
    {
      id: "creds_sap_odata",
      kind: "credentials",
      vendor: "sap_odata",
      title: msg`Connessione SAP OData`,
      description: msg`Endpoint OData v4, credenziali tecniche e namespace del servizio SAP.`,
      fields: sapOdataFields,
    },
    {
      id: "creds_zucchetti",
      kind: "credentials",
      vendor: "zucchetti",
      title: msg`Connessione Zucchetti`,
      description: msg`Endpoint API Zucchetti — disponibile dopo la firma del contratto partner.`,
      fields: partnerGatedFields,
    },
    {
      id: "creds_teamsystem",
      kind: "credentials",
      vendor: "teamsystem",
      title: msg`Connessione TeamSystem`,
      description: msg`Endpoint API TeamSystem — disponibile dopo la firma del contratto partner.`,
      fields: partnerGatedFields,
    },
  ],

  benefits: [
    {
      icon: <Server className={iconCls} aria-hidden />,
      title: msg`ERP italiano sincronizzato`,
      body: msg`Anagrafiche, ordini, scadenze e magazzino disponibili ad Ando in lettura.`,
    },
    {
      icon: <Layers className={iconCls} aria-hidden />,
      title: msg`Schema auto-discovery`,
      body: msg`Tabelle e relazioni profilate al primo collegamento — nessuna mappatura manuale.`,
    },
    {
      icon: <Search className={iconCls} aria-hidden />,
      title: msg`Query AI sui dati ERP`,
      body: msg`Chiedi ad Ando di interrogare il tuo ERP in linguaggio naturale.`,
    },
    {
      icon: <Sparkles className={iconCls} aria-hidden />,
      title: msg`Cross-vendor unified`,
      body: msg`Stessa esperienza tra SAP, Zucchetti e TeamSystem — vista normalizzata.`,
    },
  ],

  api: createCapabilityApi<ErpConnection>({ capability: "erp" }),

  connectionKeyField: "id" as keyof ErpConnection,
  getDisplayName: (c) => {
    if (c.service_url) {
      try {
        return new URL(c.service_url).hostname;
      } catch {
        return c.service_url;
      }
    }
    if (c.host) return c.host;
    return c.id ?? "ERP";
  },
  getSecondaryId: (c) => c.connector_type ?? c.vendor ?? null,
};
